import type { StaffingRequirementsApiResponse } from '../components/projects-management/services/staffingRequirementsService';
import { DedupedGet, DedupedUrlCache, SharedInFlight } from './dedupedFetch';
import type { ScopedRevenueSummary } from './scopedRevenueSummary';

/**
 * Cross-page dedupe + short-TTL caching layer for list endpoints that are
 * requested with **byte-identical URLs** from more than one page.
 *
 * Per the project direction ("Option B"), caches are only shared at this
 * level when the URL matches exactly; page-specific query-string variants
 * stay in their page-local service (e.g. `/allocations?limit=1000` lives in
 * `timelineDataService`, `/employees?include_absences=false&only_consultants=true`
 * would live in `planningBoardDataService`, etc.).
 *
 * `staffingRequirementsWithRates` is opt-in: callers pass this cache into
 * `fetchStaffingRequirementsWithRates` / `fetchStaffingRequirementsForMultipleProjects`
 * to dedupe read-only fan-outs (e.g. `BaseTimeline`). Mutation-adjacent call
 * sites deliberately omit it and keep fetching fresh.
 */
export const commonDataService = {
  projects: new DedupedGet<unknown>('/projects'),
  employeesNoAbsences: new DedupedGet<unknown>('/employees?include_absences=false'),
  allocations: new DedupedGet<unknown>('/allocations'),
  jobTitleDepartmentMappings: new DedupedGet<unknown[]>(
    '/mappings/job-title-departments',
  ),
  // Parametric by `startDate`/`endDate`; Dashboard and Reports both hit this
  // endpoint with their own ranges, and any pair of callers using the SAME
  // range will share a cache slot.
  teamUtilisation: new DedupedUrlCache<unknown>(),
  // Parametric by range (+ optional department); the Dashboard alert and the
  // Planning Board Conflicts surfaces both consume the shared summary
  // (item 4), and same-range callers share a slot. Routed here so a
  // StrictMode/remount double-mount coalesces onto one request.
  conflictsSummary: new DedupedUrlCache<unknown>(),
  staffingRequirementsWithRates: new DedupedUrlCache<StaffingRequirementsApiResponse>(),
  // The scoped revenue summary, keyed by scope and range: the Dashboard and the
  // Reports Overview each read their own scope (AC-59). In-flight sharing only,
  // no TTL: a figure read before a save must never be shown after it, so two
  // screens asking at once share one request and the next ask reaches the wire.
  scopedRevenueSummary: new SharedInFlight<ScopedRevenueSummary>(),
  /**
   * A project's billing schedule + rollup, keyed by
   * `/api/projects/:projectId/billing` (payment milestones PM-27).
   *
   * The Billing tab is mounted only while it is the active tab — that is what
   * makes its fetch lazy (AC-18) — so without a cache every visit to the tab
   * is a fresh mount and a fresh request for a schedule that has not changed.
   * The api-efficiency pass measured a second full fetch on each revisit.
   *
   * **Every milestone write clears it** (`useBillingMutations` → `refetch`), so
   * the TTL never serves a schedule the user has just edited. That
   * invalidation is the whole reason this is opt-in rather than automatic:
   * a cached billing figure that outlives its edit is worse than an extra
   * request.
   */
  projectBilling: new DedupedUrlCache<unknown>(),
};

/**
 * Clear every cache managed by this service. Invoked from each page's own
 * `clearXDataCache()` so a user-driven refresh always bypasses the TTL.
 */
export function clearCommonDataCache(): void {
  commonDataService.projects.clear();
  commonDataService.employeesNoAbsences.clear();
  commonDataService.allocations.clear();
  commonDataService.jobTitleDepartmentMappings.clear();
  commonDataService.teamUtilisation.clear();
  commonDataService.conflictsSummary.clear();
  commonDataService.staffingRequirementsWithRates.clear();
  commonDataService.scopedRevenueSummary.clear();
  commonDataService.projectBilling.clear();
}
